/**
 * Service-worker cache rules, as pure dependency-injected functions.
 *
 * Everything that decides *what is written to disk and what is served from
 * it* lives here rather than in `src/sw.js`, for one reason: this module
 * touches no worker global. It never reads `self`, `caches`, `clients` or
 * the real `fetch`; those all arrive through a context object. That makes
 * every rule below runnable under Vitest + jsdom against a fake
 * `CacheStorage`, so the guarantees can be asserted on *cache contents*
 * instead of on the names of the strategies that produced them. See
 * `src/serviceWorker/cacheRules.test.js`.
 *
 * The worker this replaces (`public/sw.js`, deleted in task 5.3a) never
 * cached anything: its precache list held Create React App paths
 * (`/static/js/bundle.js`) that no Vite build emits, and `cache.addAll` is
 * atomic, so a single 404 rejected the whole install. Two habits below come
 * straight out of that post-mortem:
 *
 *   1. The precache list is generated from the real build output
 *      (`self.__WB_MANIFEST`, injected by vite-plugin-pwa) and is never
 *      hand-maintained.
 *   2. Precaching uses per-entry `Promise.allSettled`, not `cache.addAll`,
 *      so one bad entry degrades the cache instead of disabling it.
 *
 * A third habit is a *reading* one. The old worker was full of
 * `somePromise || fallback`, which is always truthy and so silently made
 * the fallback dead code. Every branch here awaits explicitly.
 */

/**
 * Cache names written by the pre-5.3a worker. Real devices still carry
 * these. They are swept by name on activate because nothing else will ever
 * clean them up -- and `canvas-dynamic-v1` in particular is load-bearing:
 * the old worker served same-origin hashed assets from it cache-first and
 * forever, so a returning user can otherwise stay pinned to pre-5.3a JS.
 */
export const LEGACY_CACHE_NAMES = Object.freeze([
  'canvas-companion-v1',
  'canvas-static-v1',
  'canvas-dynamic-v1',
]);

/** Every cache this app has ever owned starts with this prefix. */
export const CACHE_PREFIX = 'canvas-';

/** Header stamped onto cached API responses to record when they were fetched. */
export const CACHED_AT_HEADER = 'x-canvas-cached-at';

/** postMessage type used to hand the last-synced timestamp to the page. */
export const LAST_SYNCED_MESSAGE_TYPE = 'canvas:last-synced';

/** postMessage type the page sends to drop the API cache on sign-out. */
export const PURGE_API_CACHE_MESSAGE_TYPE = 'canvas:purge-api-cache';

/**
 * Reply the worker posts back through the message's port once the purge has
 * actually happened.
 *
 * The acknowledgement is not decoration. `postMessage` to a worker is
 * fire-and-forget, so a sign-out that posts and walks away cannot tell the
 * difference between "purged" and "the page was torn down first" -- and
 * neither can a test, which is how a purge silently stops happening while
 * everything stays green. The page awaits this before it finishes signing
 * out.
 */
export const PURGE_API_CACHE_ACK_TYPE = 'canvas:purge-api-cache-ack';

/** The routing decision for a request. */
export const ROUTE = Object.freeze({
  /** Credential-bearing endpoints. Network only; nothing is ever written. */
  AUTH: 'auth',
  /** Reads that must always be live. Network only; never stored. */
  NO_STORE: 'no-store',
  /** A page navigation. Served from the precached app shell. */
  NAVIGATE: 'navigate',
  /** A same-origin static asset. Served from precache, else network. */
  ASSET: 'asset',
  /** A GET read of the app's own API. Stale-while-revalidate. */
  API: 'api',
  /** Not ours. The worker does not call respondWith at all. */
  PASSTHROUGH: 'passthrough',
});

/**
 * Endpoints that hand out or verify credentials. These are matched *first*,
 * ahead of every other rule including the method test, so that no later
 * branch can reach them. `/credentials` is the sharpest of them: it returns
 * the user's Canvas LMS API token.
 *
 * Note this is deliberately a path deny-list and not a header test. Every
 * authenticated request the app makes carries `Authorization: Bearer ...`
 * (src/services/api.ts) and every guest request carries `X-Canvas-Token`,
 * so "never cache a request bearing an auth header" would cache nothing at
 * all and there would be no offline data to show.
 */
const AUTH_PATH_PATTERNS = [
  /(^|\/)credentials(\/|$)/,
  /(^|\/)get-canvas-credentials(\/|$)/,
  /(^|\/)validate-token(\/|$)/,
  /(^|\/)refresh-token(\/|$)/,
];

/** AWS Cognito's own hosts, which the auth SDK talks to directly. */
const AUTH_HOST_PATTERNS = [/^cognito-idp\./i, /^cognito-identity\./i];

/**
 * Read paths of the app's own API (see src/services/apiServiceManager.ts).
 * Listed explicitly so the rule holds whichever origin they are served
 * from: cross-origin in production, same-origin behind the dev proxy. The
 * API being on another origin is a safety property, not the rule.
 */
const API_PATH_PATTERNS = [
  /^\/courses(\/|$)/,
  /^\/snapshots(\/|$)/,
  /^\/assignment-notes(\/|$)/,
  /^\/health(\/|$)/,
  /^\/guest\/courses(\/|$)/,
  /^\/guest\/snapshots(\/|$)/,
];

/**
 * Reads that must never be served from a cache.
 *
 * `version.json` is the app's update check, so a cached copy would answer
 * "is there a new build?" with the old build's answer.
 *
 * `/preferences` is settings data. apiServiceManager's own contract for it
 * reads "Never cached - this is settings data the user expects to see
 * reflect their latest save immediately" (task 3.5), and stale-while-
 * revalidate would show the pre-save values on the next screen.
 */
const NO_STORE_PATH_PATTERNS = [/^\/version\.json$/, /^\/preferences(\/|$)/];

/** Also excluded from the precache, for the same reason. */
const VERSION_PATH = '/version.json';

function isAuthUrl(url) {
  if (AUTH_HOST_PATTERNS.some((pattern) => pattern.test(url.hostname))) {
    return true;
  }
  return AUTH_PATH_PATTERNS.some((pattern) => pattern.test(url.pathname));
}

function isApiPath(url) {
  return API_PATH_PATTERNS.some((pattern) => pattern.test(url.pathname));
}

function isNoStorePath(url) {
  return NO_STORE_PATH_PATTERNS.some((pattern) => pattern.test(url.pathname));
}

/**
 * Decide how a request should be handled.
 *
 * Requests are duck-typed rather than required to be real `Request`
 * objects: anything with `url`, `method` and `mode` works. (`Request` cannot
 * be constructed with `mode: 'navigate'` in any engine, so navigation tests
 * would be impossible otherwise.)
 *
 * @param {{url: string, method?: string, mode?: string}} request
 * @param {{appOrigin: string, apiOrigins?: string[]}} config
 * @returns {string} a ROUTE value
 */
export function classifyRequest(request, config = {}) {
  const { appOrigin, apiOrigins = [] } = config;

  let url;
  try {
    url = new URL(request.url);
  } catch {
    return ROUTE.PASSTHROUGH;
  }

  // First rule, deliberately: auth endpoints, before origin or method is
  // even considered. Nothing below can widen this.
  if (isAuthUrl(url)) return ROUTE.AUTH;

  // Only reads are ever cacheable.
  const method = (request.method || 'GET').toUpperCase();
  if (method !== 'GET') return ROUTE.PASSTHROUGH;

  const sameOrigin = url.origin === appOrigin;
  const isAppApi = sameOrigin || apiOrigins.includes(url.origin);

  // Checked before the API rule, so a never-store path cannot be picked up
  // by stale-while-revalidate.
  if (isAppApi && isNoStorePath(url)) return ROUTE.NO_STORE;

  if (isApiPath(url) && isAppApi) return ROUTE.API;

  if (sameOrigin && request.mode === 'navigate') return ROUTE.NAVIGATE;

  if (sameOrigin) return ROUTE.ASSET;

  return ROUTE.PASSTHROUGH;
}

/**
 * Query parameter carrying the identity fingerprint on an API cache key.
 *
 * The Cache API matches on URL, but this app's identity does not live in
 * the URL: apiServiceManager.buildHeaders puts the child's Canvas user id
 * in `X-Canvas-User-Id`, and the signed-in parent is identified only by the
 * `Authorization` bearer token. `GET /courses` is therefore byte-identical
 * for every child of every parent. Without this, switching child -- the
 * app's main navigation -- serves the previous child's course list off
 * disk, and the side-by-side view (task 5.2b) collapses N parallel reads
 * onto one entry. That is the same cross-child bleed tasks 4.4b, 5.2a and
 * 5.2c exist to prevent, one layer below their query keys.
 */
const IDENTITY_PARAM = '__canvas_identity';

function headerValue(request, name) {
  const headers = request && request.headers;
  if (!headers || typeof headers.get !== 'function') return '';
  return headers.get(name) || '';
}

/**
 * The `sub` claim of a Cognito ID token: a stable, opaque per-user id.
 *
 * The bearer token itself is deliberately not used as identity. It rotates
 * on every refresh, which would strand each previous build of the cache and
 * break offline continuity an hour into a session.
 */
function jwtSubject(authorization) {
  const token = authorization.replace(/^Bearer\s+/i, '');
  const segments = token.split('.');
  if (segments.length !== 3) return '';

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded));
    return claims && claims.sub ? String(claims.sub) : '';
  } catch {
    return '';
  }
}

/**
 * Everything that makes one caller's copy of a response different from
 * another's. Secrets are folded in only as fingerprints, never verbatim --
 * cache keys are readable in devtools.
 */
function identityOf(request) {
  const authorization = headerValue(request, 'authorization');
  const canvasToken = headerValue(request, 'x-canvas-token');

  const account = authorization
    ? jwtSubject(authorization) || `bearer:${fingerprint(authorization)}`
    : `canvas:${fingerprint(canvasToken)}`;

  return [
    account,
    headerValue(request, 'x-canvas-user-id'),
    headerValue(request, 'x-canvas-root-url'),
  ].join('|');
}

/**
 * The cache key for an API read: its URL plus a fingerprint of the identity
 * that asked for it. Two children hitting the same URL get two entries.
 */
export function apiCacheKey(request) {
  const url = new URL(request.url);
  url.searchParams.set(IDENTITY_PARAM, fingerprint(identityOf(request)));
  return url.href;
}

function offlineResponse(message) {
  return new Response(JSON.stringify({ error: 'offline', detail: message }), {
    status: 503,
    statusText: 'Offline',
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Read the stamp written by {@link putStamped}. Returns null when absent. */
export function readCachedAt(response) {
  const raw = response && response.headers && response.headers.get(CACHED_AT_HEADER);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Store a response, stamped with the moment it came off the network. The
 * stamp is what makes "last synced" meaningful: on a later cache hit it
 * says how old the data on screen actually is, rather than reporting the
 * time of the hit itself.
 */
async function putStamped(cache, key, response, at) {
  const headers = new Headers(response.headers);
  headers.set(CACHED_AT_HEADER, String(at));
  const body = await response.clone().arrayBuffer();
  await cache.put(
    key,
    new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers,
    })
  );
}

/**
 * Same-origin static assets: precache, else straight to the network.
 *
 * This handler deliberately never writes. The old worker's cache-first
 * branch wrote every same-origin asset into a long-lived runtime cache and
 * then preferred it forever, which is how a user gets pinned to stale JS.
 * Here the only asset cache is the precache, which is rebuilt per build and
 * swept on activate.
 */
async function handleAsset(request, ctx) {
  const cache = await ctx.caches.open(ctx.precacheName);
  const cached = await cache.match(request.url);
  if (cached) return cached;

  try {
    return await ctx.fetch(request);
  } catch (error) {
    return offlineResponse(String(error));
  }
}

/** Navigations are served from the precached app shell so offline works. */
async function handleNavigate(request, ctx) {
  const cache = await ctx.caches.open(ctx.precacheName);
  const shell = await cache.match(ctx.shellUrl);
  if (shell) return shell;

  try {
    return await ctx.fetch(request);
  } catch (error) {
    return offlineResponse(String(error));
  }
}

/**
 * API reads: stale-while-revalidate.
 *
 * Serve the cached copy immediately when there is one, refresh it in the
 * background, and report the age of whatever was served so the app can show
 * a last-synced time. When there is no cached copy this degrades to a plain
 * network read, and to a 503 body (never a thrown rejection) when offline.
 */
async function handleApiRead(request, ctx) {
  const cache = await ctx.caches.open(ctx.apiCacheName);
  // Keyed by URL *and* caller identity -- see apiCacheKey. Keying on the
  // URL alone would serve one child's data to the next.
  const key = apiCacheKey(request);
  const cached = await cache.match(key);

  const revalidate = (async () => {
    const response = await ctx.fetch(request);
    if (response && response.ok) {
      const at = ctx.now();
      await putStamped(cache, key, response, at);
      ctx.broadcastLastSynced(at, request.url);
    }
    return response;
  })();

  if (cached) {
    // The refresh is genuinely in the background here, so its failure must
    // not surface as an unhandled rejection.
    revalidate.catch(() => {});
    ctx.broadcastLastSynced(readCachedAt(cached), request.url);
    return cached;
  }

  try {
    return await revalidate;
  } catch (error) {
    return offlineResponse(String(error));
  }
}

/**
 * Apply the routing table to one request.
 *
 * AUTH and NO_STORE are handled explicitly rather than by omission. The
 * pre-5.3a worker never cached auth responses either, but only because the
 * API happened to sit on another origin and its cross-origin guard returned
 * early -- an accident, not a rule. These two branches make it a rule: they
 * fetch, and they touch no cache.
 *
 * @param {object} request
 * @param {object} ctx caches, fetch, now, broadcastLastSynced, cache names
 * @returns {Promise<Response>}
 */
export async function handleRequest(request, ctx) {
  const route = classifyRequest(request, ctx);

  switch (route) {
    case ROUTE.ASSET:
      return handleAsset(request, ctx);
    case ROUTE.NAVIGATE:
      return handleNavigate(request, ctx);
    case ROUTE.API:
      return handleApiRead(request, ctx);
    case ROUTE.AUTH:
    case ROUTE.NO_STORE:
    case ROUTE.PASSTHROUGH:
    default:
      // Network only. No cache is opened, read or written on this path.
      return ctx.fetch(request);
  }
}

/**
 * Delete caches this app owns that are not in `keepNames` -- including, by
 * name, the three caches left behind by the pre-5.3a worker.
 *
 * Workbox-style precaching only ever cleans up its own caches, so without
 * this sweep the legacy three survive indefinitely on real devices.
 *
 * @returns {Promise<string[]>} the names actually deleted
 */
export async function purgeStaleCaches(cacheStorage, keepNames = []) {
  const existing = await cacheStorage.keys();
  const doomed = existing.filter((name) => {
    if (keepNames.includes(name)) return false;
    return LEGACY_CACHE_NAMES.includes(name) || name.startsWith(CACHE_PREFIX);
  });

  await Promise.all(doomed.map((name) => cacheStorage.delete(name)));
  return doomed;
}

/**
 * Drop the API cache on sign-out. A sibling of `purgeStaleCaches`, not a
 * branch of it: that one sweeps caches this *build* has finished with, on
 * activate. This one removes a *user's* data when their session ends, and
 * the two answer to entirely different events.
 *
 * Task 5.3a is what made this necessary. Before it nothing was cached at
 * all -- the worker's install always failed and the API was cross-origin
 * and never intercepted -- so there was nothing to leave behind. Now course
 * and grade data persists on disk, which is the point of the task, and on a
 * shared device it would otherwise outlive the session that fetched it.
 *
 * **The whole cache goes, not just the signing-out identity's entries.**
 * The identity fingerprint (`identityOf`) is computed from request
 * *headers* -- the bearer token's `sub`, `X-Canvas-User-Id`,
 * `X-Canvas-Root-Url` -- and the page cannot faithfully reproduce them at
 * sign-out. A parent holds one entry per child, and sessionStorage (where
 * the child ids live) is being torn down at exactly that moment, so a
 * per-identity purge would miss whichever entries it could no longer name
 * and would do so silently. Rebuilding the key format page-side would also
 * duplicate the keying task 5.3a settled. Whole-cache is a strict superset
 * of the narrow purge, and the two failure modes are not comparable:
 * over-deleting costs one refetch, under-deleting leaves a signed-out
 * user's grades on the device.
 *
 * The precache is deliberately untouched. The app shell is not user data,
 * and dropping it would break the offline load 5.3a exists to provide.
 *
 * @param {CacheStorage} cacheStorage
 * @param {string} apiCacheName
 * @returns {Promise<boolean>} whether a cache was actually there to delete
 */
export async function purgeApiCache(cacheStorage, apiCacheName) {
  // `delete` resolves false for a cache that was never opened, so signing
  // out before any API read is a no-op rather than an error.
  return cacheStorage.delete(apiCacheName);
}

/**
 * Populate the precache from the build-generated manifest.
 *
 * Per-entry and settled, not `cache.addAll`: addAll is atomic, and its
 * all-or-nothing failure is precisely why the previous worker cached
 * nothing at all for its entire life.
 *
 * @returns {Promise<{cached: string[], failed: string[]}>}
 */
export async function precacheAssets(cacheStorage, cacheName, urls, ctx) {
  const cache = await cacheStorage.open(cacheName);

  const results = await Promise.allSettled(
    urls.map(async (url) => {
      const response = await ctx.fetch(url, { cache: 'reload' });
      if (!response || !response.ok) {
        throw new Error(`precache miss: ${url} (${response && response.status})`);
      }
      await cache.put(url, response);
      return url;
    })
  );

  const cached = [];
  const failed = [];
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') cached.push(urls[index]);
    else failed.push(urls[index]);
  });

  return { cached, failed };
}

/**
 * Resolve the injected precache manifest to absolute URLs.
 *
 * `version.json` is dropped: it is the app's update check, and a precached
 * copy would answer the "is there a new build?" question with the old
 * build's answer.
 *
 * @param {Array<{url: string, revision?: string|null}>} manifest
 * @param {string} baseURL
 */
export function manifestUrls(manifest, baseURL) {
  const urls = (manifest || [])
    .map((entry) => (typeof entry === 'string' ? entry : entry && entry.url))
    .filter(Boolean)
    .map((url) => new URL(url, baseURL).href)
    .filter((url) => !url.endsWith(VERSION_PATH));

  return Array.from(new Set(urls));
}

/**
 * Derive a precache name from the manifest's own content.
 *
 * The cache name has to change whenever the build does, or unhashed entries
 * -- `index.html` above all -- would collide with the previous build's copy
 * inside a fixed cache name and be served cache-first forever. Hashing the
 * manifest ties the cache's identity to the exact asset set it holds, and
 * `purgeStaleCaches` removes the previous one on activate.
 */
export function precacheName(manifest, prefix = 'canvas-precache-') {
  const seed = (manifest || [])
    .map((entry) =>
      typeof entry === 'string' ? entry : `${entry.url}:${entry.revision || ''}`
    )
    .sort()
    .join('|');

  return `${prefix}${fingerprint(seed)}`;
}

/**
 * FNV-1a, 32-bit. Not a security hash -- a short, stable, opaque
 * fingerprint for cache naming and cache keys.
 */
function fingerprint(value) {
  let hash = 0x811c9dc5;
  const text = String(value);
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
